import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import { FormHelper } from "utils";
import { GridView } from "components";
import { Perfil } from "models/Perfil";
import useMessage from "hooks/useMessage";
import useQueryParams from "hooks/useQueryParams";
import { PerfilManager, FuncionManager } from "managers";
import useExceptionHandler from "hooks/useExceptionHandler";
import RadioGroup from "components/RadioGroup/RadioGroup";
import EditHeader from "components/PantallasEdit/Header/EditHeader";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import { TIPO_SERIE_FISCAL_LIST, TIPO_SERIE_FISCAL_FACTURA_CREDITO_FISCAL } from "utils";
import { DateHelper, StyleHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { SerieFiscalManager } from "managers";

export default function SerieFiscalEdit({ showLoading, hideLoading }) {
  const [serieFiscal, setSerieFiscal] = useState({});
  const [esNuevo, setEsNuevo] = useState(false);
  const [tituloHeader, setTituloHeader] = useState("Crear Serie Fiscal");
  const [funcionesPorAgregarList, setFuncionesPorAgregarList] = useState([]);
  const URLParameters = useQueryParams();
  const { Warning, Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();

  const Id = URLParameters.get("Id");
  const esClonar = URLParameters.get("clonar") == "true";
  const ESTATUS_LIST = Perfil.ESTATUS_LIST;
  const ESTATUS_ACTIVO = "A";
  const location = useLocation();

  useEffect(() => {
    setEsNuevo(!Id || esClonar);
    if (Id) {
      showLoading();
      cargarSerieFiscal(Id);
      esClonar
        ? setTituloHeader("Clonar Serie Fiscal")
        : setTituloHeader("Editar  Serie Fiscal");
    } else {
      setDefaultValues();
    }

    return () => {
      hideLoading();
      cerrarAlertas();
    };
  }, [location.search]);

  const setDefaultValues = () => {
    setSerieFiscal({
      ...serieFiscal,
      Tipo: TIPO_SERIE_FISCAL_FACTURA_CREDITO_FISCAL,
      Estatus: Perfil.DEFAULT_ESTATUS_VALUE,
      LlamadaCondicion: Perfil.CONDICION_LLAMADA_EN_PROCESO,
    });
  };

  const cargarSerieFiscal = (Id) => {
    SerieFiscalManager.buscarPorId(Id)
      .then((data) => {
        setSerieFiscal(data);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  };

  const validar = () => {
    const validaciones = [];
    if (!serieFiscal.Tipo) {
      validaciones.push("Debe seleccionar el tipo de la Serie Fiscal.");
    }

    if (!serieFiscal.FechaExpiracion) {
      validaciones.push(
        "Debe seleccionar la Fecha de Expiración de la Serie Fiscal."
      );
    }

    if (!serieFiscal.RangoDesde) {
      validaciones.push(
        "Debe seleccionar el incio del rango de las secuencias."
      );
    }

    if (serieFiscal.RangoDesde <= 0) {
      validaciones.push("El incio del rango de las secuencias es invalido.");
    }

    if (!serieFiscal.RangoHasta) {
      validaciones.push("Debe seleccionar el fin del rango de las secuencias.");
    }

    if (serieFiscal.RangoHasta <= 0) {
      validaciones.push("El fin del rango de las secuencias es invalido.");
    }

    if (parseInt(serieFiscal.RangoHasta) <= parseInt(serieFiscal.RangoDesde)) {
      validaciones.push("El rango seleccionado es invalido.");
    }

    if (validaciones.length >= 1) {
      Warning({ message: validaciones[0] });
      return false;
    }
    return true;
  };

  const grabar = () => {
    showLoading();
    if (!validar()) {
      hideLoading();
      return;
    }
    if (esNuevo) {
      SerieFiscalManager.crear(serieFiscal)
        .then((res) => {
          setEsNuevo(false);
          Message({ message: `Grabado Exitosamente` });
          cargarSerieFiscal(res.IdSec);
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    } else {
      SerieFiscalManager.actualizar(serieFiscal)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarSerieFiscal(res.IdSec);
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    }
  };

  function addToList(Id) {
    const index = funcionesPorAgregarList.findIndex((funcion) => {
      return funcion.IdSec === Id;
    });
    serieFiscal.Funciones.push(funcionesPorAgregarList[index]);
  }

  function removeFromList(Id) {
    const index = serieFiscal.Funciones.findIndex((perfil) => {
      return perfil.IdSec === Id;
    });
    setFuncionesPorAgregarList([
      ...funcionesPorAgregarList,
      serieFiscal.Funciones[index],
    ]);
    serieFiscal.Funciones.splice(index, 1);
  }

  function handleFuncionSwitch(params) {
    const { Id } = params;
    const index = funcionesPorAgregarList.findIndex((funcion) => {
      return funcion.IdSec === Id;
    });
    let list = [...funcionesPorAgregarList];
    if (list[index].EsAnadirPerfil) {
      removeFromList(Id);
    } else {
      addToList(Id);
    }
    list[index].EsAnadirPerfil = !funcionesPorAgregarList[index].EsAnadirPerfil;
    setFuncionesPorAgregarList(list);
  }

  const inactivar = () => {
    showLoading();

    SerieFiscalManager.inactivar(serieFiscal.IdSec)
      .then(() => {
        Message({ message: `Secuencia Fiscal Inactivada Exitosamente` });
        cargarSerieFiscal(res.IdSec);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  };

  return (
    <Form action="" className="form-horizontal" id="TypeValidation" method="">
      <Card>
        <EditHeader
          titulo={`${tituloHeader}`}
          volver={true}
          grabar={grabar}
          inactivar={!esNuevo && inactivar}
        />
        <EditContainer>
          <EditSection title="Información General" style={{}}>
            <SectionRow multilinea>
              <InputContainer stacked label="Tipo de Comprobante">
                <RadioGroup
                  options={TIPO_SERIE_FISCAL_LIST}
                  selectedOption={serieFiscal.Tipo}
                  disabled={!esNuevo}
                  onChange={(e) => {
                    setSerieFiscal({
                      ...serieFiscal,
                      Tipo: e.target.value
                    });
                  }}
                />
              </InputContainer>
            </SectionRow>

            <SectionRow columns={3}>
              <InputContainer stacked label="Fecha Expiración">
                <DateTimePicker
                  usarLabel={false}
                  esRangoFechas={false}
                  labelFechaInicio=""
                  soloFecha={true}
                  required={true}
                  fechaInicioOriginal={serieFiscal.FechaExpiracion}
                  onChange={(e) => {
                    if (
                      serieFiscal.FechaExpiracion !==
                      DateHelper.formatDate(e.fechaInicio) &&
                      e.fechaInicio
                    ) {
                      setSerieFiscal({
                        ...serieFiscal,
                        FechaExpiracion: DateHelper.formatDate(e.fechaInicio),
                      });
                    }
                  }}
                />
              </InputContainer>

              <InputContainer stacked label="Estatus">
                <RadioGroup
                  options={ESTATUS_LIST}
                  selectedOption={serieFiscal.Estatus || ESTATUS_ACTIVO}
                  disabled={true}
                  onChange={(e) => {
                    setSerieFiscal({
                      ...serieFiscal,
                      Estatus: e.target.value
                    });
                  }}
                />
              </InputContainer>
            </SectionRow>
          </EditSection>

          <EditSection title="Información del Rango de Serie" style={{}}>
            <Row className="align-items-end">
              <Col md={2}>
                <InputContainer stacked label="Serie Fija">
                  <Form.Control
                    name="SerieFija"
                    type="text"
                    disabled={true}
                    onChange={(e) => FormHelper.handleChange(e, setSerieFiscal)}
                    value={serieFiscal.Tipo ? "E" + serieFiscal.Tipo : ""}
                  />
                </InputContainer>
              </Col>

              <Col md={2}>
                <InputContainer stacked label="Rango Desde">
                  <Form.Control
                    name="RangoDesde"
                    type="number"
                    onChange={(e) => FormHelper.handleChange(e, setSerieFiscal)}
                    value={serieFiscal.RangoDesde}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </Col>

              <Col md={2}>
                <InputContainer stacked label="Rango Hasta">
                  <Form.Control
                    name="RangoHasta"
                    type="number"
                    onChange={(e) => FormHelper.handleChange(e, setSerieFiscal)}
                    value={serieFiscal.RangoHasta}
                    style={StyleHelper.InputRequired}
                  />
                </InputContainer>
              </Col>

              <Col md={2}>
                <InputContainer stacked label="Última Serie Usada">
                  <Form.Control
                    disabled={esNuevo}
                    name="UltimaSerieUsada"
                    type="number"
                    onChange={(e) => FormHelper.handleChange(e, setSerieFiscal)}
                    value={serieFiscal.UltimaSerieUsada}
                  />
                </InputContainer>
              </Col>
            </Row>
          </EditSection>
        </EditContainer>
      </Card>
    </Form>
  );
}
